import React from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import {
  Modal,
  Portal
} from 'react-native-paper';
import {
  type ReviewItem,
  type ReviewTemplate
} from './shareItemMapping';
import {
  MetadataOnlyShareSection,
  NewItemQuestionCopySection
} from './shareItemCopySections';
import { ExistingItemQuestionMappingSection } from './shareItemMappingSections';
import {
  ShareItemDetoxControls,
  ShareItemLoadingOverlay,
  ShareItemModalActions,
  ShareItemModalHeader
} from './shareItemModalChrome';
import {
  CreateTargetItemForm,
  TargetCategorySelector,
  TargetItemSelector,
  TargetTemplateSelector
} from './shareItemTargetSections';
import { useShareItemModalState } from './useShareItemModalState';

interface ShareItemModalProps {
  visible: boolean;
  onDismiss: () => void;
  sourceItem: ReviewItem;
  sourceTemplateId: number;
  templates: ReviewTemplate[];
  onSuccess: () => void;
}

/**
 * Modal for linking items across templates and mapping questions
 */
export const ShareItemModal: React.FC<ShareItemModalProps> = ({
  visible,
  onDismiss,
  sourceItem,
  sourceTemplateId,
  templates,
  onSuccess
}) => {
  const {
    availableTemplates,
    canCreateLink,
    categoryMenuVisible,
    createItemMode,
    creatingItem,
    detoxControlsEnabled,
    firstAvailableTemplate,
    fullTemplate,
    getCompatibleTargets,
    handleAddMapping,
    handleCancelCreateItem,
    handleClose,
    handleCreateNewItem,
    handleDetoxCreateFirstLink,
    handleRemoveMapping,
    handleSelectCategory,
    handleSelectFirstTarget,
    handleSelectItem,
    handleSelectTemplate,
    handleStartCreateItem,
    handleSubmit,
    handleToggleCopy,
    handleToggleIncludeFloor,
    handleToggleIncludePhotos,
    handleToggleIncludeRoom,
    handleUpdateMapping,
    includeFloor,
    includePhotos,
    includeRoom,
    isNewItem,
    itemMenuVisible,
    loading,
    loadingTemplate,
    newItemDescription,
    newItemName,
    questionMappings,
    questionsToCopy,
    selectedCategory,
    selectedTemplateName,
    setCategoryMenuVisible,
    setItemMenuVisible,
    setNewItemDescription,
    setNewItemName,
    setSourceMappingMenuId,
    setTargetMappingMenuId,
    setTemplateMenuVisible,
    sourceFields,
    sourceMappingMenuId,
    targetCategories,
    targetCategoryId,
    targetItem,
    targetItemId,
    targetItems,
    targetMappingMenuId,
    targetTemplateId,
    templateMenuVisible
  } = useShareItemModalState({
    sourceItem,
    sourceTemplateId,
    templates,
    onDismiss,
    onSuccess
  });

  return (
    <Portal>
      <Modal
        testID="share-item-modal"
        visible={visible}
        onDismiss={handleClose}
        contentContainerStyle={styles.modal}
      >
        <ScrollView>
          <ShareItemModalHeader sourceItemName={sourceItem.name} />

          <ShareItemDetoxControls
            enabled={detoxControlsEnabled}
            canSelectFirstTarget={!targetTemplateId && Boolean(firstAvailableTemplate)}
            canCreateSelectedLink={Boolean(targetItemId)}
            canCreateFirstLink={Boolean(targetTemplateId && fullTemplate && !loadingTemplate)}
            loading={loading}
            onSelectFirstTarget={handleSelectFirstTarget}
            onCreateSelectedLink={handleSubmit}
            onCreateFirstLink={handleDetoxCreateFirstLink}
          />

          {/* Template Selector */}
          <TargetTemplateSelector
            availableTemplates={availableTemplates}
            selectedTemplateId={targetTemplateId}
            selectedTemplateName={selectedTemplateName}
            menuVisible={templateMenuVisible}
            onOpenMenu={() => setTemplateMenuVisible(true)}
            onDismissMenu={() => setTemplateMenuVisible(false)}
            onSelectTemplate={handleSelectTemplate}
          />

          {/* Category Selector */}
          {targetTemplateId && (
            <TargetCategorySelector
              categories={targetCategories}
              selectedCategoryId={targetCategoryId}
              selectedCategoryName={selectedCategory?.name}
              loadingTemplate={loadingTemplate}
              menuVisible={categoryMenuVisible}
              onOpenMenu={() => setCategoryMenuVisible(true)}
              onDismissMenu={() => setCategoryMenuVisible(false)}
              onSelectCategory={handleSelectCategory}
            />
          )}

          {/* Item Selector */}
          {targetCategoryId && !createItemMode && (
            <TargetItemSelector
              items={targetItems}
              selectedItemId={targetItemId}
              selectedItemName={targetItem?.name}
              menuVisible={itemMenuVisible}
              onOpenMenu={() => setItemMenuVisible(true)}
              onDismissMenu={() => setItemMenuVisible(false)}
              onSelectItem={handleSelectItem}
              onCreateNewItem={handleStartCreateItem}
            />
          )}

          {/* Create New Item Form */}
          {targetCategoryId && createItemMode && (
            <CreateTargetItemForm
              newItemName={newItemName}
              newItemDescription={newItemDescription}
              creatingItem={creatingItem}
              onChangeName={setNewItemName}
              onChangeDescription={setNewItemDescription}
              onCancel={handleCancelCreateItem}
              onCreate={handleCreateNewItem}
            />
          )}

          {/* Metadata-only section when source item has no questions */}
          {targetItemId && targetItem && sourceFields.length === 0 && (
            <MetadataOnlyShareSection
              includeRoom={includeRoom}
              includePhotos={includePhotos}
              includeFloor={includeFloor}
              onToggleRoom={handleToggleIncludeRoom}
              onTogglePhotos={handleToggleIncludePhotos}
              onToggleFloor={handleToggleIncludeFloor}
            />
          )}

          {/* Question Selection: Different UI for new vs existing items */}
          {targetItemId && targetItem && isNewItem && sourceFields.length > 0 && (
            <NewItemQuestionCopySection
              targetItem={targetItem}
              sourceFields={sourceFields}
              questionsToCopy={questionsToCopy}
              includeRoom={includeRoom}
              includePhotos={includePhotos}
              includeFloor={includeFloor}
              onToggleRoom={handleToggleIncludeRoom}
              onTogglePhotos={handleToggleIncludePhotos}
              onToggleFloor={handleToggleIncludeFloor}
              onToggleCopy={handleToggleCopy}
            />
          )}

          {/* Question Mappings: For existing items */}
          {targetItemId && targetItem && !isNewItem && sourceFields.length > 0 && (
            <ExistingItemQuestionMappingSection
              targetItem={targetItem}
              sourceFields={sourceFields}
              questionMappings={questionMappings}
              questionsToCopy={questionsToCopy}
              includeRoom={includeRoom}
              includePhotos={includePhotos}
              includeFloor={includeFloor}
              sourceMappingMenuId={sourceMappingMenuId}
              targetMappingMenuId={targetMappingMenuId}
              getCompatibleTargets={getCompatibleTargets}
              onToggleRoom={handleToggleIncludeRoom}
              onTogglePhotos={handleToggleIncludePhotos}
              onToggleFloor={handleToggleIncludeFloor}
              onUpdateMapping={handleUpdateMapping}
              onRemoveMapping={handleRemoveMapping}
              onAddMapping={handleAddMapping}
              onToggleCopy={handleToggleCopy}
              onOpenSourceMappingMenu={setSourceMappingMenuId}
              onDismissSourceMappingMenu={() => setSourceMappingMenuId(null)}
              onOpenTargetMappingMenu={setTargetMappingMenuId}
              onDismissTargetMappingMenu={() => setTargetMappingMenuId(null)}
            />
          )}

          <ShareItemModalActions
            loading={loading}
            canCreateLink={canCreateLink}
            onCancel={handleClose}
            onCreateLink={handleSubmit}
          />

          <ShareItemLoadingOverlay visible={loading} />
        </ScrollView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    padding: 20,
    margin: 20,
    borderRadius: 8,
    maxHeight: '90%',
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%'
  }
});

export default ShareItemModal;
